import { useContext, useMemo, useState } from "react";
import { Flex, Text } from "@chakra-ui/react";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronRight } from "lucide-react";

import { DataTable, palette } from "@/containers/app/summary/components/ui";
import TablesStateContext from "@/contexts/TablesStateContext";
import { money } from "@/hooks/useNumberFormatter";
import { usePortalContext } from "@/hooks/usePortalContext";

export type OrgMerchantRow = {
  id: string;
  displayName: string;
  collected: number;
  paidInvoices: number;
};

type SortKey = "displayName" | "collected" | "paidInvoices";
type Sort = { key: SortKey; dir: "asc" | "desc" };

const COLUMNS: { key: SortKey; label: string; numeric: boolean }[] = [
  { key: "displayName", label: "COMERCIO", numeric: false },
  { key: "collected", label: "RECAUDO", numeric: true },
  { key: "paidInvoices", label: "FACTURAS PAGADAS", numeric: true },
];

/**
 * The trailing chevron column. Narrow rather than an equal quarter of the
 * table, which is what `tableLayout="fixed"` would give it otherwise.
 */
const OPEN_COLUMN_WIDTH = "36px";
const COLUMN_WIDTHS = [...COLUMNS.map(() => undefined), OPEN_COLUMN_WIDTH];

/**
 * A column header that sorts. Deliberately not the merchant dashboard's
 * `PriorityHeader`: that one is typed on its own sort-key union and carries a
 * filter popover per column, so sharing it would mean refactoring a live
 * screen. If a third table wants one, this is what moves into `components/ui`.
 */
const SortableHeader = ({
  label,
  sortKey,
  sort,
  numeric,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  sort: Sort;
  numeric: boolean;
  onSort: (key: SortKey) => void;
}): JSX.Element => {
  const active = sort.key === sortKey;

  return (
    <Flex
      align="center"
      gap={1}
      cursor="pointer"
      userSelect="none"
      justify={numeric ? "flex-end" : "flex-start"}
      color={active ? palette.purple : undefined}
      onClick={() => onSort(sortKey)}
    >
      <Text fontSize="11px" fontWeight="700">
        {label}
      </Text>
      {active ? (
        sort.dir === "asc" ? (
          <ArrowUp size={11} />
        ) : (
          <ArrowDown size={11} />
        )
      ) : (
        <ArrowUpDown size={11} opacity={0.4} />
      )}
    </Flex>
  );
};

export const OrgCollectionTable = ({
  merchants,
}: {
  merchants: OrgMerchantRow[];
}): JSX.Element => {
  const [sort, setSort] = useState<Sort>({ key: "collected", dir: "desc" });
  const { setContext } = usePortalContext();
  const { resetAllTables } = useContext(TablesStateContext);

  const openMerchant = (merchantId: string): void => {
    resetAllTables();
    setContext({ type: "merchant", id: merchantId });
  };

  const toggleSort = (key: SortKey): void =>
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
        : // Names read naturally ascending; amounts and counts descending.
          { key, dir: key === "displayName" ? "asc" : "desc" }
    );

  const rows = useMemo(() => {
    const factor = sort.dir === "asc" ? 1 : -1;

    // Name is the tie-break on every sort, so a month where half the portfolio
    // sits at zero still has a stable, readable order.
    return [...merchants].sort((a, b) => {
      if (sort.key === "displayName")
        return factor * a.displayName.localeCompare(b.displayName);

      const delta = factor * (a[sort.key] - b[sort.key]);
      return delta !== 0 ? delta : a.displayName.localeCompare(b.displayName);
    });
  }, [merchants, sort]);

  if (merchants.length === 0)
    return (
      <Text color={palette.muted} fontSize="13px" py={6} textAlign="center">
        Esta organización todavía no tiene comercios.
      </Text>
    );

  return (
    <DataTable
      columnWidths={COLUMN_WIDTHS}
      onRowClick={(rowIndex) => openMerchant(rows[rowIndex].id)}
      headers={[
        ...COLUMNS.map((column) => (
          <SortableHeader
            key={column.key}
            label={column.label}
            sortKey={column.key}
            sort={sort}
            numeric={column.numeric}
            onSort={toggleSort}
          />
        )),
        // Header for the chevron column: empty, but present so the header and
        // cell counts match.
        <span key="open" />,
      ]}
      rows={rows.map((merchant) => [
        <Text fontSize="12px" color={palette.text} fontWeight="500" truncate>
          {merchant.displayName}
        </Text>,
        <Text fontSize="12px" textAlign="right">
          {money(merchant.collected)}
        </Text>,
        <Text fontSize="12px" textAlign="right">
          {merchant.paidInvoices.toLocaleString("es-CO")}
        </Text>,
        <Flex justify="flex-end" color={palette.muted}>
          <ChevronRight size={14} />
        </Flex>,
      ])}
    />
  );
};
